import { useState } from "react";
import { api, ApiError, query } from "../api/client";
import { useQuery } from "../api/useQuery";
import { useAuth } from "../auth/AuthContext";
import { fmtDateTime, fmtSize } from "../components/format";
import { PhotoThumb } from "../components/Photo";
import type { Observation, Page } from "../api/types";

const PAGE_SIZE = 24;

const KIND_NAMES: Record<string, string> = {
  direct: "прямое наблюдение",
  traces: "следы жизнедеятельности",
  remains: "останки",
  sound: "по голосу",
};

type SpeciesOption = { code: string; name: string; latin_name: string; is_protected: boolean };

/** Реестр наблюдений научного отдела: таблица или галерея, фильтры, верификация, выгрузка. */
export function ObservationsPage() {
  const { me } = useAuth();
  const [view, setView] = useState<"table" | "gallery">("table");
  const [unverified, setUnverified] = useState(false);
  const [species, setSpecies] = useState("");
  const [kind, setKind] = useState("");
  const [author, setAuthor] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [offset, setOffset] = useState(0);
  const [verifying, setVerifying] = useState<Observation | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  const speciesList = useQuery<{ items: SpeciesOption[] }>("/api/v1/species-list");
  const authors = useQuery<{ items: { id: string; full_name: string }[] }>("/api/v1/observations/authors");

  const filter = {
    unverified: unverified || undefined,
    species,
    kind,
    author_id: author,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(new Date(to).getTime() + 86400000).toISOString() : undefined,
  };
  const { data, error, loading, reload } = useQuery<Page<Observation>>(
    "/api/v1/observations" + query({ ...filter, limit: PAGE_SIZE, offset }));
  const reset = (fn: () => void) => { fn(); setOffset(0); };
  const filtered = !!(unverified || species || kind || author || from || to);

  async function download(format: "xlsx" | "csv" | "zip") {
    setExporting(format);
    setExportError(null);
    try {
      await api.download("/api/v1/export/observations" + query({ format, ...filter }), `observations.${format}`);
    } catch (e) {
      setExportError(e instanceof ApiError ? e.message : "Не удалось выгрузить");
    } finally {
      setExporting(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Реестр наблюдений</h2>
          {data && <p className="muted small">всего: {data.total}</p>}
        </div>
        <div className="filters" style={{ margin: 0 }}>
          <div className="segmented" role="group" aria-label="Вид">
            <button aria-pressed={view === "table"} onClick={() => setView("table")}>Таблица</button>
            <button aria-pressed={view === "gallery"} onClick={() => setView("gallery")}>Галерея</button>
          </div>
        </div>
      </div>

      <div className="filters">
        <label className="field">С даты
          <input type="date" value={from} onChange={(e) => reset(() => setFrom(e.target.value))} />
        </label>
        <label className="field">По дату
          <input type="date" value={to} onChange={(e) => reset(() => setTo(e.target.value))} />
        </label>
        <label className="field">Вид
          <select value={species} onChange={(e) => reset(() => setSpecies(e.target.value))}>
            <option value="">Все виды</option>
            {speciesList.data?.items.map((sp) => <option key={sp.code} value={sp.code}>{sp.name}</option>)}
          </select>
        </label>
        <label className="field">Тип наблюдения
          <select value={kind} onChange={(e) => reset(() => setKind(e.target.value))}>
            <option value="">Любой</option>
            {Object.entries(KIND_NAMES).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </label>
        {(authors.data?.items.length ?? 0) > 1 && (
          <label className="field">Автор
            <select value={author} onChange={(e) => reset(() => setAuthor(e.target.value))}>
              <option value="">Все</option>
              {authors.data!.items.map((a) => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
          </label>
        )}
        <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6, alignSelf: "center" }}>
          <input type="checkbox" checked={unverified} style={{ minHeight: 0 }}
            onChange={(e) => reset(() => setUnverified(e.target.checked))} />
          только без верификации
        </label>
        {filtered && (
          <button className="secondary" onClick={() => reset(() => {
            setUnverified(false); setSpecies(""); setKind(""); setAuthor(""); setFrom(""); setTo("");
          })}>Сбросить</button>
        )}
      </div>

      <div className="filters" style={{ alignItems: "center" }}>
        <span className="muted small">Выгрузить {filtered ? "отобранное" : "весь реестр"}:</span>
        {(["xlsx", "csv", "zip"] as const).map((f) => (
          <button key={f} className="secondary" disabled={exporting !== null} onClick={() => void download(f)}>
            {exporting === f ? "Готовлю…" : f === "zip" ? "Архив с фото" : f.toUpperCase()}
          </button>
        ))}
      </div>

      {error && <div className="error" role="alert">{error}</div>}
      {exportError && <div className="error" role="alert">{exportError}</div>}
      {loading && !data && <p className="notice">Загрузка…</p>}
      {data && data.items.length === 0 && <div className="card empty">Наблюдений нет.</div>}

      {verifying && (
        <VerifyForm observation={verifying} species={speciesList.data?.items ?? []}
          onDone={() => { setVerifying(null); reload(); }} onCancel={() => setVerifying(null)} />
      )}

      {data && data.items.length > 0 && (view === "table"
        ? <ObservationTable items={data.items} canVerify={!!me?.can_verify_species} onVerify={setVerifying} />
        : <ObservationGallery items={data.items} canVerify={!!me?.can_verify_species} onVerify={setVerifying} />)}

      {data && data.total > PAGE_SIZE && (
        <div className="filters" style={{ marginTop: 14 }}>
          <button className="secondary" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Назад</button>
          <span className="muted small">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} из {data.total}
          </span>
          <button className="secondary" disabled={offset + PAGE_SIZE >= data.total}
            onClick={() => setOffset(offset + PAGE_SIZE)}>Далее</button>
        </div>
      )}

      <p className="muted small" style={{ marginTop: 14 }}>
        Научная верификация записывается отдельным полем и не заменяет
        определение инспектора: исходная запись сохраняется неизменной.
      </p>
    </>
  );
}

function Coordinates({ o }: { o: Observation }) {
  if (o.lat === null || o.lon === null) return <span className="muted">нет фикса</span>;
  return (
    <>
      {o.lat.toFixed(o.location_masked ? 2 : 5)}, {o.lon.toFixed(o.location_masked ? 2 : 5)}
      {o.location_masked && (
        <div className="muted small" title="Точное место обитания охраняемого вида доступно только научному отделу и директору">
          координата загрублена
        </div>
      )}
    </>
  );
}

function Verification({ o }: { o: Observation }) {
  if (!o.verified_species_name) return <span className="muted small">не верифицировано</span>;
  return (
    <>
      {o.verified_species_name}
      <div className="muted small">{o.verified_by}{o.verification_note && ` · ${o.verification_note}`}</div>
    </>
  );
}

function ObservationTable({ items, canVerify, onVerify }: {
  items: Observation[]; canVerify: boolean; onVerify: (o: Observation) => void;
}) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Вид по определению инспектора</th>
              <th>Научное определение</th>
              <th className="num">Кол-во</th>
              <th>Тип</th>
              <th>Координаты</th>
              <th>Автор и время</th>
              <th>Фото</th>
              {canVerify && <th></th>}
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id}>
                <td>
                  {o.species_name}
                  {o.is_protected && <> <span className="badge warn">охраняемый</span></>}
                  {o.comment && <div className="muted small">{o.comment}</div>}
                </td>
                <td><Verification o={o} /></td>
                <td className="num">{o.count ?? "—"}</td>
                <td className="small">{KIND_NAMES[o.kind] ?? o.kind}</td>
                <td className="small"><Coordinates o={o} /></td>
                <td className="small">
                  {o.author_name}
                  <div className="muted">{fmtDateTime(o.device_time)}</div>
                </td>
                <td className="small">
                  {o.photos.map((p) => (
                    <div key={p.id}>
                      {p.state === "complete"
                        ? <><PhotoThumb id={p.id} /><span className="muted">{fmtSize(p.size_bytes)}</span></>
                        : <span className="badge warn">догружается</span>}
                    </div>
                  ))}
                  {o.photos.length === 0 && <span className="muted">—</span>}
                </td>
                {canVerify && (
                  <td><button className="secondary" onClick={() => onVerify(o)}>Верифицировать</button></td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ObservationGallery({ items, canVerify, onVerify }: {
  items: Observation[]; canVerify: boolean; onVerify: (o: Observation) => void;
}) {
  return (
    <div className="gallery">
      {items.map((o) => {
        const photo = o.photos.find((p) => p.state === "complete");
        return (
          <article key={o.id} className="card gallery-item">
            <div className="gallery-photo">{photo ? <PhotoThumb id={photo.id} /> : <div className="thumb">нет фото</div>}</div>
            <div className="gallery-body">
              <strong>{o.verified_species_name ?? o.species_name}</strong>
              {o.is_protected && <> <span className="badge warn">охраняемый</span></>}
              {o.verified_species_name && o.verified_species_name !== o.species_name && (
                <div className="muted small">инспектор: {o.species_name}</div>
              )}
              <div className="muted small">
                {KIND_NAMES[o.kind] ?? o.kind}{o.count ? ` · ${o.count} особ.` : ""} · {fmtDateTime(o.device_time)}
              </div>
              <div className="muted small">{o.author_name} · {o.territory_name}</div>
              {o.comment && <div className="small">{o.comment}</div>}
              {canVerify && (
                <button className="secondary" style={{ marginTop: 8 }} onClick={() => onVerify(o)}>
                  {o.verified_species_name ? "Изменить определение" : "Верифицировать"}
                </button>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

/** Научное определение — отдельной записью; определение инспектора остаётся. */
function VerifyForm({ observation, species, onDone, onCancel }: {
  observation: Observation; species: SpeciesOption[]; onDone: () => void; onCancel: () => void;
}) {
  const [code, setCode] = useState(observation.verified_species_code ?? observation.species_code);
  const [note, setNote] = useState(observation.verification_note);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/observations/${observation.id}/verify`, { species_code: code, note: note.trim() });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить определение");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" style={{ marginBottom: 16 }} aria-label="Научная верификация">
      <h3 style={{ margin: "0 0 4px" }}>Научная верификация</h3>
      <p className="muted small" style={{ margin: "0 0 12px" }}>
        Инспектор определил: {observation.species_name} · {fmtDateTime(observation.device_time)} · {observation.author_name}
      </p>
      <form onSubmit={(e) => void submit(e)} style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 560 }}>
        {error && <div className="error" role="alert">{error}</div>}
        <label className="field">Вид по определению научного отдела
          <select value={code} onChange={(e) => setCode(e.target.value)}>
            {species.map((sp) => (
              <option key={sp.code} value={sp.code}>{sp.name}{sp.latin_name ? ` (${sp.latin_name})` : ""}</option>
            ))}
          </select>
        </label>
        <label className="field">Примечание
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="filters" style={{ margin: 0 }}>
          <button type="submit" disabled={busy}>Сохранить определение</button>
          <button type="button" className="secondary" onClick={onCancel}>Отмена</button>
        </div>
      </form>
    </section>
  );
}
